import { createContext, useContext, useMemo, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { ThemeProvider } from "@mui/material";
import { setThemeMode, buildMuiTheme } from "./theme";

const STORAGE_KEY = "botriq:themeMode";
const ThemeModeContext = createContext(null);

export const useThemeMode = () => {
  const ctx = useContext(ThemeModeContext);
  if (!ctx) throw new Error("useThemeMode must be used within ThemeModeProvider");
  return ctx;
};

const readStoredMode = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved === "dark" ? "dark" : "light";
  } catch {
    return "light";
  }
};

export const ThemeModeProvider = ({ children }) => {
  const [mode, setMode] = useState(() => {
    const initial = readStoredMode();
    setThemeMode(initial); // stamp <html data-theme> before first paint
    return initial;
  });

  const toggleMode = useCallback(() => {
    setMode((prev) => {
      const next = prev === "dark" ? "light" : "dark";
      setThemeMode(next);
      try {
        localStorage.setItem(STORAGE_KEY, next);
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  // Rebuild the MUI theme object whenever mode flips (tokens are already
  // mutated by setThemeMode above at this point).
  const muiTheme = useMemo(() => buildMuiTheme(mode), [mode]);

  const value = useMemo(() => ({ mode, toggleMode, isDark: mode === "dark" }), [mode, toggleMode]);

  // No remount on toggle: tokens are now CSS var() references (see theme.js),
  // so setThemeMode flips <html data-theme> and the browser repaints instantly.
  // The MUI ThemeProvider still swaps its computed theme object per mode. This
  // avoids the old full-tree remount (which caused the reload/flash + refetch).
  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={muiTheme}>{children}</ThemeProvider>
    </ThemeModeContext.Provider>
  );
};

ThemeModeProvider.propTypes = { children: PropTypes.node.isRequired };
